// Load per-verse study-note counts for a chapter across the whole family.
// Used on the Listen/Read page to flag verses that have at least one note.
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '../config/firebase';

async function resolveFamilyMemberIds(familyIds) {
  const ids = new Set();
  for (const familyId of familyIds || []) {
    try {
      const snap = await getDocs(
        query(collection(db, 'families'), where(documentId(), '==', familyId))
      );
      if (!snap.empty) {
        const d = snap.docs[0].data();
        const members = d.memberIds || d.memberIDs || [];
        members.forEach((id) => ids.add(id));
      }
    } catch (e) {
      console.error('chapterNotes: failed to load family', familyId, e);
    }
  }
  return ids;
}

/**
 * Returns a map { [verseNumber]: noteCount } for the given book + chapter,
 * aggregated across the current user and all their family members.
 * Notes live in users/{uid}/notes (single source of truth).
 */
export async function getChapterNoteCounts(currentUser, familyIds, book, chapter) {
  if (!currentUser || !book || !chapter) return {};

  const memberIds = await resolveFamilyMemberIds(familyIds);
  memberIds.add(currentUser.uid); // include the user's own notes
  const ids = Array.from(memberIds);
  if (ids.length === 0) return {};

  const snaps = await Promise.all(
    ids.map((id) =>
      getDocs(
        query(
          collection(db, `users/${id}/notes`),
          where('book', '==', book),
          where('chapter', '==', chapter)
        )
      ).catch(() => ({ docs: [] }))
    )
  );

  const counts = {};
  snaps.forEach((snap) => {
    snap.docs.forEach((docSnap) => {
      const n = docSnap.data();
      if (n.verse) {
        counts[n.verse] = (counts[n.verse] || 0) + 1;
      } else if (n.verseRange && n.verseRange.start) {
        for (let v = n.verseRange.start; v <= n.verseRange.end; v++) {
          counts[v] = (counts[v] || 0) + 1;
        }
      }
    });
  });

  return counts;
}
